import {useConfig} from '@aboutyou/config';
import {ContentBlocks_NewsletterPushSignup} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/content_blocks.types';
import {useTranslation} from '@aboutyou/i18n';
import {useHistory} from '@aboutyou/router';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  Image,
  RowSection,
  Text,
  tabletAndUpLinaria,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useMemo, useState} from 'react';
import {useGetActiveMissionByType} from '../../../backend-services/Coins/utils/useGetActiveMissions';
import {DEVICE_PARAM_TYPE} from '../../Checkout/constants';
import {transformImage} from '../transforms';
import {NewsletterMissionBanner} from './NewsletterMissionBanner/NewsletterMissionBanner';
import {NewsletterSignupAppIntent} from './NewsletterSignupAppIntent';
import {NewsletterSubscriptionForm} from './NewsletterSubscriptionForm';
import {NewsletterSubscriptionStatus} from './NewsletterSubscriptionStatus';

interface NewsletterPushSignupProps {
  content: ContentBlocks_NewsletterPushSignup;
}

export const NewsletterPushSignup: React.FC<NewsletterPushSignupProps> = ({
  content: {headline, subline, cta, image, isSubscribed},
}) => {
  const transformedImage = image.url ? transformImage(image) : undefined;
  const [submitSuccessful, setSubmitSuccessful] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const {t} = useTranslation();
  const {device} = useConfig();
  const history = useHistory();

  const appDevice: DEVICE_PARAM_TYPE = useMemo(() => {
    const queryParams = new URLSearchParams(history.location.search);
    const device = queryParams.get('device');

    return device === 'ios' || device === 'android' ? device : null;
  }, [history]);

  const showForm =
    !appDevice && !isSubscribed && !submitSuccessful && !submitError;

  const isNewsletterMissionActive = useGetActiveMissionByType({
    mission: 'newsletterMission',
  });

  const onNewsletterSubscriptionSuccess = () => {
    setSubmitSuccessful(true);
  };

  return (
    <StyledRowSection>
      <Container>
        {device === 'mobile' && <NewsletterMissionBanner />}
        <ImageContainer>
          {transformedImage && (
            <StyledImage
              alt={transformedImage.description}
              breakpoints={[
                {columns: 1},
                {
                  breakpoint: '(max-width: 767px)',
                  columns: 2,
                },
              ]}
              ratio={transformedImage.aspectRatio}
              src={image.url}
              loading="eager"
            />
          )}
          {device === 'mobile' && (
            <>
              <TopGradient />
              <BottomGradient />
              <Headline>{headline}</Headline>
            </>
          )}
        </ImageContainer>

        <ContentContainer
          data-is-mission-active={Boolean(isNewsletterMissionActive)}
        >
          {device === 'desktop' && <NewsletterMissionBanner />}
          <Content>
            {showForm && (
              <>
                {device === 'desktop' && <Headline>{headline}</Headline>}
                {subline && <Subline as="p">{subline}</Subline>}
                <StyledNewsletterSubscriptionForm
                  cta={cta || t('Notify me')}
                  onSuccess={onNewsletterSubscriptionSuccess}
                  onError={() => setSubmitError(true)}
                />
              </>
            )}

            {submitSuccessful && (
              <StyledNewsletterSubscriptionStatus
                type="success"
                headline={t('Just one step left!')}
                subline={t(
                  'Please confirm your registration by clicking on the link in the email we have sent you.',
                )}
              />
            )}

            {submitError && (
              <StyledNewsletterSubscriptionStatus
                type="error"
                headline={t('Oops, something went wrong.')}
                subline={t('Please reload the page and try again.')}
              />
            )}

            {isSubscribed && (
              <StyledNewsletterSubscriptionStatus
                type="success"
                headline={t(
                  "Good news: You're already subscribed to our newsletter.",
                )}
                subline={t('Thanks for being part of our fashion family!')}
              />
            )}

            {appDevice && (
              <>
                {subline && <Subline as="p">{subline}</Subline>}
                <NewsletterSignupAppIntent
                  cta={cta || t('Notify me')}
                  device={appDevice}
                />
              </>
            )}
          </Content>
        </ContentContainer>
      </Container>
    </StyledRowSection>
  );
};

const StyledRowSection = styled(RowSection)`
  ${tabletAndUpLinaria(`
    margin: 48px auto 24px auto;
  `)}
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;

  ${tabletAndUpLinaria(`
    flex-direction: row;
    border-radius: 16px;
    overflow: hidden;

    > div {
      width: 50%;
    }
  `)}
`;

const ContentContainer = styled.div`
  ${tabletAndUpLinaria(`
    border-top-right-radius: 16px;
    border-bottom-right-radius: 16px;
    border: 1px solid ${COLORS['Outline.Grey']};
    border-left: none;

    &[data-is-mission-active='true'] {
      border-top: none;
    }
  `)}
`;

const Content = styled.div`
  padding: 0 16px;

  ${tabletAndUpLinaria(`
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: 420px;
    padding: 32px 24px;
  `)}
`;

const ImageContainer = styled.div`
  position: relative;
`;

const StyledImage = styled(Image)`
  object-fit: cover;

  ${tabletAndUpLinaria(`
    position: absolute;
    height: 100%;
  `)}
`;

const TopGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 30%;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.5) 0%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const BottomGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 58%;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.6) 25%,
    rgba(0, 0, 0, 0) 100%
  );
`;

const Headline = styled(Text)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 32px;
  text-align: center;
  font-size: ${FONT_SIZES.px24};
  font-weight: ${FONT_WEIGHTS.heavy};
  line-height: 1.2;
  color: ${COLORS['Text.White']};

  ${tabletAndUpLinaria(`
    position: static;
    color: ${COLORS['Text.Black']};
    padding: 0;
    text-align: left;
  `)}
`;

const Subline = styled(Text)`
  padding: 24px 0;
  margin: 0;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.4;

  ${tabletAndUpLinaria(`
    padding: 12px 0 0 0;
  `)}
`;

const StyledNewsletterSubscriptionForm = styled(NewsletterSubscriptionForm)`
  padding-top: 16px;

  ${tabletAndUpLinaria(`
    padding-top: 32px;
  `)}
`;

const StyledNewsletterSubscriptionStatus = styled(NewsletterSubscriptionStatus)`
  margin: 64px 0 56px 0;
`;
